import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  Box,
  TableBody,
  IconButton,
  TableFooter,
  Icon,
  Grid,
  Chip,
  Tooltip,
  Divider,
} from "@mui/material";
import { capitalCase } from "change-case";
import SingleChoiceFilter from "components/filters/SingleChoiceFilter/SingleChoiceFilter";
import MDAlert from "components/MDAlert";
import MDBox from "components/MDBox";
import TableSkeleton from "components/TableSkeleton/TableSkeleton";
import dayjs from "dayjs";
import React, { useCallback, useEffect } from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { StyledTableRow } from "./JobsSchedules/JobsSchedules";
import { visuallyHidden } from "@mui/utils";
import useJobPhasesTableData from "hooks/jobs/useJobPhasesTableData";
import { GetJobsSortKey, Proposal, SortDirection } from "generated/graphql";
import MDTypography from "components/MDTypography";
import WorkOrderModal from "./WorkOrder/WorkOrderModal";
import JobCostModal from "./WorkOrder/JobCostModal";
import MDButton from "components/MDButton";
import Modal from "modules/Modal/Modal";
import CircularProgress from "@mui/material/CircularProgress";
import useGetJobLazy from "hooks/jobs/useGetJobLazy";
import View from "modules/jobs/View";
import { Job } from "generated/graphql";
import MultipleChoiceFilter from "components/filters/MultipleChoiceFilter/MultipleChoiceFilter";
import { enumToValueAsKeyOptions } from "utils/enums/enumToValueAsKeyOptions";
import useJobsBillingStatuses from "hooks/jobs/useJobsBillingStatuses";
import { equals, reject, uniq } from "ramda";
import { getJobCostingStatusMeta } from "utils/jobs/jobCostingVisibility";
import formatCentsToUSD from "utils/money/formatCentsToUSD";

export default function ListJobPhases() {
  const {
    queryLoading,
    queryError,
    pagination,
    sorting,
    companiesData,
    companiesLoading,
    companiesError,
    filterControl,
    filtering,
    jobPhasesData: jobPhases,
    serachBox,
  } = useJobPhasesTableData();

  const [openStatus, setOpenStatus] = useState<Record<string, boolean | undefined>>({});
  const [jobPhasesIds, setJobPhasesID] = useState<Array<Proposal["id"]>>([]);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [jobIdSelected, setJobIdSelected] = useState<string>("");
  const [jobSelected, setJobSelected] = useState<Job | null>(null);
  const { data: enumBillingStatus } = useJobsBillingStatuses();
  const [getJob] = useGetJobLazy();

  const handleToggle = useCallback((id: Proposal["id"]) => {
    setOpenStatus((prevState) => ({
      ...prevState,
      [id]: typeof prevState[id] === "undefined" ? true : !prevState[id],
    }));
  }, []);

  const handleExpandAll = useCallback((ids: Array<Proposal["id"]>) => {
    ids.forEach((id) => {
      setOpenStatus((prevState) => ({
        ...prevState,
        [id]: typeof prevState[id] === "undefined" ? true : !prevState[id],
      }));
    });
  }, []);

  const handleClickJobId = (jobId: string) => {
    setJobIdSelected(jobId);
    setOpenModal(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      const jobInfo = await getJob(jobIdSelected);
      setJobSelected(jobInfo?.data?.getJob as Job);
    };

    if (jobIdSelected) {
      fetchData();
    }
  }, [jobIdSelected]);

  const onClose = () => {
    setJobIdSelected("");
    setJobSelected(null);
    setOpenModal(false);
  };

  useEffect(() => {
    const jobIds = jobPhases?.map((jobPhase) => {
      return jobPhase?.id;
    });
    setJobPhasesID(jobIds);
  }, [jobPhases]);

  const getJobPhaseStartsAt = (filterDate, scheduledBlocks, jobPhaseDate): string[] => {
    if (filterDate && scheduledBlocks?.length > 0) {
      return scheduledBlocks?.map((block) => dayjs.utc(block.scheduledAt).local().format("h:mm A"));
    }

    if (jobPhaseDate) {
      return [dayjs.utc(jobPhaseDate).local().format("h:mm A")];
    }

    return ["N/A"];
  };

  const handleBillingStatusChecked = useCallback(
    (selectedOptions) => {
      return filtering.setBillingStatuses(selectedOptions);
    },
    [filtering]
  );

  const renderJobCostingTooltip = (jobPhase) => {
    return (
      <MDBox py={0.5}>
        <MDTypography variant="caption" display="block" color="white">
          Proposed Total: {formatCentsToUSD(Number(jobPhase.proposedCostTotal ?? 0))}
        </MDTypography>
        <MDTypography variant="caption" display="block" color="white">
          Actual Total: {formatCentsToUSD(Number(jobPhase.actualCostTotal ?? 0))}
        </MDTypography>
        <MDTypography variant="caption" display="block" color="white">
          Variance: {jobPhase.jobCostingVariancePercent.toFixed(2)}%
        </MDTypography>
      </MDBox>
    );
  };

  const getVarianceColor = (jobPhase) => {
    const actualTotal = Number(jobPhase.actualCostTotal ?? 0);
    const proposedTotal = Number(jobPhase.proposedCostTotal ?? 0);

    if (actualTotal < proposedTotal) {
      return "success";
    }

    if (actualTotal > proposedTotal) {
      return "error";
    }

    return "text";
  };

  return (
    <>
      <Modal open={openModal} onClose={onClose} styles={{ maxWidth: "auto" }}>
        <Box sx={{ p: 3, mx: "auto" }}>
          <MDTypography variant="h4" fontWeight="medium" mb={1}>
            <a
              href={`/jobs/${jobIdSelected}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "inherit" }}
            >
              Update Job # {jobIdSelected}
            </a>
          </MDTypography>
          {jobSelected ? (
            <View job={jobSelected as Job} />
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="65vh">
              <CircularProgress color="secondary" size={110} />
            </Box>
          )}
        </Box>
      </Modal>
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: 2,
          borderRadius: "16px",
          border: "1px solid #e3e8ef",
          background:
            "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)",
        }}
      >
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} xl={8}>
            {filterControl}
          </Grid>
          <Grid item xs={12} xl={4}>
            <MDBox
              display="flex"
              justifyContent={{ xs: "flex-start", xl: "flex-end" }}
              gap={1}
              flexWrap="wrap"
            >
              <MDButton
                color="blue"
                size="small"
                sx={{ px: 1.75, py: 0.75, minWidth: 0 }}
                onClick={() => handleExpandAll(jobPhasesIds)}
              >
              Expand All
              </MDButton>
              <MDButton
                color="blue"
                size="small"
                sx={{ px: 1.75, py: 0.75, minWidth: 0 }}
                onClick={() => setOpenStatus({})}
              >
              Collapse All
              </MDButton>
            </MDBox>
          </Grid>
          <Grid item xs={12}>
            {serachBox}
          </Grid>
        </Grid>
      </Paper>
      {(queryLoading || queryError) && (
        <div style={{ padding: "16px" }}>
          {queryLoading && <TableSkeleton numberOfLines={pagination.pageSize} />}
          {queryError && <MDAlert color="error">{queryError.message}</MDAlert>}
        </div>
      )}
      {!queryLoading && !queryError && (
        <TableContainer
          component={Paper}
          sx={{ borderRadius: "18px", border: "1px solid #e3e8ef", overflow: "hidden" }}
        >
          <Table
            size="small"
            sx={{
              minWidth: 0,
              width: "100%",
              tableLayout: "fixed",
              "& th, & td": {
                px: 1,
                py: 1.1,
                fontSize: "13px",
                whiteSpace: "nowrap",
                verticalAlign: "middle",
              },
              "& th": {
                fontSize: "12px",
              },
            }}
            aria-label="simple table"
          >
            <TableHead sx={{ display: "table-header-group" }}>
              <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                <TableCell
                  align="left"
                  sortDirection={
                    sorting.sortKey === GetJobsSortKey.ID ? SortDirection[sorting.sortKey] : false
                  }
                  sx={{ width: "8%" }}
                >
                  <TableSortLabel
                    active={sorting.sortKey === GetJobsSortKey.ID}
                    direction={
                      sorting.sortKey === GetJobsSortKey.ID
                        ? (sorting.sortDirection.toLowerCase() as "asc" | "desc")
                        : "asc"
                    }
                    onClick={() => {
                      const isAsc =
                        sorting.sortKey === GetJobsSortKey.ID &&
                        sorting.sortDirection === SortDirection.ASC;
                      sorting.setSort({
                        sortDirection: isAsc ? SortDirection.DESC : SortDirection.ASC,
                        sortKey: GetJobsSortKey.ID,
                      });
                    }}
                  >
                    <MDBox display="flex" alignItems="center">Job</MDBox>
                    {sorting.sortKey === GetJobsSortKey.ID ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sorting.sortDirection === SortDirection.DESC
                          ? "sorted descending"
                          : "sorted ascending"}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>

                <TableCell sx={{ width: "11%" }} align="left">
                  <MDBox display="flex" alignItems="center">Stage</MDBox>
                </TableCell>
                <TableCell sx={{ width: "11%" }}>
                  <MDBox display={"flex"}>
                    <TableSortLabel>
                      <MDBox display="flex" alignItems="center">Phase</MDBox>
                    </TableSortLabel>
                  </MDBox>
                </TableCell>
                <TableCell
                  sx={{ width: "13%" }}
                  align="left"
                  sortDirection={
                    sorting.sortKey === GetJobsSortKey.COMPANY
                      ? SortDirection[sorting.sortKey]
                      : false
                  }
                >
                  <MDBox display={"flex"}>
                    <TableSortLabel
                      active={sorting.sortKey === GetJobsSortKey.COMPANY}
                      direction={
                        sorting.sortKey === GetJobsSortKey.COMPANY
                          ? (sorting.sortDirection.toLowerCase() as "asc" | "desc")
                          : "asc"
                      }
                      onClick={() => {
                        const isAsc =
                          sorting.sortKey === GetJobsSortKey.COMPANY &&
                          sorting.sortDirection === SortDirection.ASC;
                        sorting.setSort({
                          sortDirection: isAsc ? SortDirection.DESC : SortDirection.ASC,
                          sortKey: GetJobsSortKey.COMPANY,
                        });
                      }}
                    >
                      <MDBox display="flex" alignItems="center">Company</MDBox>
                      {sorting.sortKey === GetJobsSortKey.COMPANY ? (
                        <Box component="span" sx={visuallyHidden}>
                          {sorting.sortDirection === SortDirection.DESC
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      ) : null}
                    </TableSortLabel>

                    <SingleChoiceFilter
                      id={"company.name"}
                      value={filtering.companyId}
                      loading={companiesLoading}
                      error={companiesError}
                      options={
                        companiesData?.map((company) => ({
                          label: company.name,
                          value: company.id,
                        })) ?? []
                      }
                      onChange={(selectedCompanyId) =>
                        filtering.setCompanyID((prevState) =>
                          prevState == selectedCompanyId ? null : selectedCompanyId
                        )
                      }
                    />
                  </MDBox>
                </TableCell>
                <TableCell sx={{ width: "10%" }} align="left">
                  <MDBox display="flex" alignItems="center">
                    Billing
                  </MDBox>
                  <MultipleChoiceFilter
                    id={"job.billing_status"}
                    value={filtering.billingStatuses}
                    options={enumToValueAsKeyOptions(enumBillingStatus)}
                    onChange={handleBillingStatusChecked}
                  />
                </TableCell>
                <TableCell sx={{ width: "6%" }} align="center">
                  <MDBox display="flex" alignItems="center" justifyContent="center">
                    Costed
                  </MDBox>
                </TableCell>
                <TableCell sx={{ width: "8%" }} align="center">
                  <MDBox display="flex" alignItems="center" justifyContent="center">
                    Variance
                  </MDBox>
                </TableCell>
                <TableCell sx={{ width: "12%" }} align="left">
                  <MDBox display="flex" alignItems="center">Status</MDBox>
                </TableCell>
                <TableCell
                  sx={{ width: "11%" }}
                  align="center"
                  sortDirection={
                    sorting.sortKey === GetJobsSortKey.START
                      ? SortDirection[sorting.sortKey]
                      : false
                  }
                >
                  <TableSortLabel
                    active={sorting.sortKey === GetJobsSortKey.START}
                    direction={
                      sorting.sortKey === GetJobsSortKey.START
                        ? (sorting.sortDirection.toLowerCase() as "asc" | "desc")
                        : "asc"
                    }
                    onClick={() => {
                      const isAsc =
                        sorting.sortKey === GetJobsSortKey.START &&
                        sorting.sortDirection === SortDirection.ASC;
                      sorting.setSort({
                        sortDirection: isAsc ? SortDirection.DESC : SortDirection.ASC,
                        sortKey: GetJobsSortKey.START,
                      });
                    }}
                  >
                    <MDBox display="flex" alignItems="center" justifyContent="center">
                      Start Time
                    </MDBox>
                    {sorting.sortKey === GetJobsSortKey.START ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sorting.sortDirection === SortDirection.DESC
                          ? "sorted descending"
                          : "sorted ascending"}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left" sx={{ width: "10%" }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobPhases?.map((jobPhase, i) => {
                return (
                  <React.Fragment key={jobPhase.id}>
                    <StyledTableRow
                      className={i % 2 === 0 ? "outer even" : "outer odd"}
                      key={jobPhase.id}
                    >
                      <TableCell sx={{ position: "relative" }}>
                        <MDBox display="flex">
                          <IconButton sx={{ p: 0 }} onClick={() => handleToggle(jobPhase.id)}>
                            <Icon
                              className={`chevron ${
                                openStatus[jobPhase.id] ? "expand_more" : "expand_less"
                              }`}
                            >
                              expand_less
                            </Icon>
                          </IconButton>
                          <MDBox>
                            <Link
                              to="#"
                              style={{ color: "inherit" }}
                              onClick={() => handleClickJobId(jobPhase.job.id)}
                            >
                              {jobPhase.job.externalId}
                            </Link>
                          </MDBox>
                        </MDBox>
                      </TableCell>
                      <TableCell>
                        <MDBox sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                          {jobPhase.proposalStage.name}
                        </MDBox>
                      </TableCell>
                      <TableCell>
                        <MDBox sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                          {jobPhase.productName}
                        </MDBox>
                      </TableCell>
                      <TableCell>
                        <MDBox sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                          {jobPhase.job.company.name}
                        </MDBox>
                      </TableCell>
                      <TableCell>
                        {jobPhase.job?.billingStatus
                          ? capitalCase(jobPhase.job?.billingStatus)
                          : ""}
                      </TableCell>
                      <TableCell align="center">
                        <MDTypography
                          fontSize="13px"
                          fontWeight="medium"
                          color={jobPhase.hasJobCostsEntered ? "success" : "error"}
                        >
                          {jobPhase.hasJobCostsEntered ? "Y" : "N"}
                        </MDTypography>
                      </TableCell>
                      <TableCell align="center">
                        <MDTypography
                          fontSize="13px"
                          fontWeight="medium"
                          color={getVarianceColor(jobPhase)}
                        >
                          {jobPhase.jobCostingVariancePercent.toFixed(2)}%
                        </MDTypography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={renderJobCostingTooltip(jobPhase)} arrow placement="top">
                          <Chip
                            label={getJobCostingStatusMeta(jobPhase.jobCostingCompletionStatus).label}
                            size="small"
                            sx={{
                              color: getJobCostingStatusMeta(jobPhase.jobCostingCompletionStatus)
                                .chipColor,
                              backgroundColor: getJobCostingStatusMeta(
                                jobPhase.jobCostingCompletionStatus
                              ).chipBackground,
                              fontWeight: 600,
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ textAlign: "center", fontSize: "13px" }}>
                        <MDBox display="flex" flexDirection="column" gap={0.5}>
                          {getJobPhaseStartsAt(
                            filtering.startsAt,
                            jobPhase.scheduledBlockForDate,
                            jobPhase.startsAt
                          ).map((startAt, startIndex) => {
                            if (startAt === "N/A") {
                              return (
                                <MDTypography key={`${jobPhase.id}-start-${startIndex}`} fontSize="13px">
                                  N/A
                                </MDTypography>
                              );
                            }

                            return (
                              <MDBox key={`${jobPhase.id}-start-${startIndex}`}>
                                <MDTypography display="block" fontSize="13px" fontWeight="medium">
                                  {startAt}
                                </MDTypography>
                              </MDBox>
                            );
                          })}
                        </MDBox>
                      </TableCell>
                      <TableCell align="right" sx={{ pr: 0.75 }}>
                        <MDBox
                          display={"flex"}
                          alignItems="center"
                          justifyContent="flex-end"
                          flexDirection="row"
                          gap={0.5}
                        >
                          <WorkOrderModal jobPhaseId={jobPhase.id} />
                          <JobCostModal jobId={jobPhase.job.id} jobPhaseId={jobPhase.id} />
                        </MDBox>
                      </TableCell>
                    </StyledTableRow>
                    {openStatus[jobPhase.id] && (
                      <TableRow key={jobPhase.id}>
                        <TableCell colSpan={10} sx={{ p: 0, borderBottom: "none" }}>
                          <MDBox
                            px={3}
                            py={2.5}
                            sx={{
                              backgroundColor: "#fbfcfe",
                              borderTop: "1px solid #eef2f7",
                            }}
                          >
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6} lg={3}>
                                <MDTypography fontSize="12px" color="text" textTransform="uppercase">
                                  Phase Hours
                                </MDTypography>
                                <MDTypography fontSize="14px" fontWeight="medium">
                                  {filtering.startsAt
                                    ? jobPhase.scheduledBlockForDate.reduce(
                                        (accumulator, currentValue) =>
                                          accumulator + currentValue.blockLength,
                                        0
                                      )
                                    : jobPhase.jobDuration}
                                </MDTypography>
                              </Grid>
                              <Grid item xs={12} sm={6} lg={3}>
                                <MDTypography fontSize="12px" color="text" textTransform="uppercase">
                                  Crew Needed
                                </MDTypography>
                                <MDTypography fontSize="14px" fontWeight="medium">
                                  {jobPhase?.crew?.name ?? "N/A"}
                                </MDTypography>
                              </Grid>
                              <Grid item xs={12} sm={6} lg={3}>
                                <MDTypography fontSize="12px" color="text" textTransform="uppercase">
                                  Material Type
                                </MDTypography>
                                <MDTypography fontSize="14px" fontWeight="medium">
                                  {jobPhase.organizationProductTypeName}
                                </MDTypography>
                              </Grid>
                              <Grid item xs={12} sm={6} lg={3}>
                                <MDTypography fontSize="12px" color="text" textTransform="uppercase">
                                  Material Needed
                                </MDTypography>
                                <MDTypography fontSize="14px" fontWeight="medium">
                                  {jobPhase.proposedMaterialNeeded}
                                </MDTypography>
                              </Grid>
                            </Grid>
                            <Divider sx={{ my: 2 }} />
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={4}>
                                <MDTypography fontSize="12px" color="text" textTransform="uppercase">
                                  Equipment Needed
                                </MDTypography>
                                <MDBox component="ul" pl={2.5} mb={0}>
                                  {jobPhase.defaultEquipmentPieces.map((equipment) => (
                                    <li key={equipment.id}>
                                      <MDTypography fontSize="14px">{equipment.name}</MDTypography>
                                    </li>
                                  ))}
                                </MDBox>
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <MDTypography fontSize="12px" color="text" textTransform="uppercase">
                                  Travel Time (One Way)
                                </MDTypography>
                                <MDTypography fontSize="14px" fontWeight="medium">
                                  {jobPhase.proposalStage.distanceSeconds
                                    ? (jobPhase.proposalStage.distanceSeconds / 3600).toFixed(2) +
                                      " Hour(s)"
                                    : "-"}
                                </MDTypography>
                              </Grid>
                              <Grid item xs={12} md={2}>
                                <MDTypography fontSize="12px" color="text" textTransform="uppercase">
                                  Job Size
                                </MDTypography>
                                <MDTypography fontSize="14px" fontWeight="medium">
                                  {jobPhase.jobSize}
                                </MDTypography>
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <MDTypography fontSize="12px" color="text" textTransform="uppercase">
                                  Contact Details
                                </MDTypography>
                                <MDTypography fontSize="14px" fontWeight="medium">
                                  {jobPhase.job.contact?.firstName +
                                    " " +
                                    jobPhase.job.contact?.lastName}
                                </MDTypography>
                                <MDTypography fontSize="14px" color="text">
                                  {jobPhase.job.contact.phone}
                                </MDTypography>
                              </Grid>
                            </Grid>
                          </MDBox>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={10}>
                  <MDBox sx={{ display: "flex", justifyContent: "flex-end" }}>
                    {pagination.component}
                  </MDBox>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
    </>
  );
}
